import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Divider,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import { timeClockAPI } from '../services/api';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';
import { formatDate, parseServerUtc } from '../utils/dateFormat';
import { useSkeletonAnimation } from '../components/LoadingSkeletons';
import {
  tablePageRootSx,
  tablePageScrollRegionSx,
  tablePageTableContainerSx,
} from '../theme/tablePageLayout';

const formatHours = (hours) => `${Number(hours || 0).toFixed(2)} hrs`;

// Digits keep their width, so hour figures line up and the timer doesn't jitter.
const tabularSx = { fontVariantNumeric: 'tabular-nums' };

function BreakdownRow({ label, value, strong = false }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', py: 0.25 }}>
      <Typography variant="body1" color={strong ? 'text.primary' : 'text.secondary'} fontWeight={strong ? 600 : 400}>
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={strong ? 700 : 500} sx={tabularSx}>
        {value}
      </Typography>
    </Box>
  );
}

const formatTime = (value) => {
  const date = parseServerUtc(value);
  return date ? date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '-';
};

// mm/dd without the year, so the shift table fits a phone's width.
const formatShortDate = (value) => {
  const date = parseServerUtc(value);
  return date ? date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' }) : '-';
};

// h:mm:ss since `start`, ticking once a second while the worker is clocked in.
function useElapsed(start) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!start) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [start]);

  const began = parseServerUtc(start);
  if (!began) return null;
  const totalSeconds = Math.max(0, Math.floor((now - began.getTime()) / 1000));
  const h = Math.floor(totalSeconds / 3600);
  const m = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
  const s = String(totalSeconds % 60).padStart(2, '0');
  return `${h}:${m}:${s}`;
}

function TimeClockSkeleton() {
  const animation = useSkeletonAnimation();
  return (
    <Box aria-busy="true" aria-label="Loading…">
      <Skeleton animation={animation} variant="rounded" height={160} sx={{ mb: 2 }} />
      <Skeleton animation={animation} variant="rounded" height={80} sx={{ mb: 2 }} />
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} animation={animation} variant="rounded" height={56} sx={{ mb: 1 }} />
      ))}
    </Box>
  );
}

export default function TimeClockPage() {
  const notify = useNotify();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [punching, setPunching] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await timeClockAPI.me();
      setSummary(res.data);
    } catch (error) {
      notify.error(errorMessage(error, 'Failed to load your time clock'));
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const openEntry = summary?.open_entry;
  const elapsed = useElapsed(openEntry?.clock_in);

  const handlePunch = async () => {
    setPunching(true);
    try {
      if (openEntry) {
        await timeClockAPI.clockOut();
      } else {
        await timeClockAPI.clockIn();
      }
      await load();
    } catch (error) {
      notify.error(errorMessage(error, openEntry ? 'Clock out failed' : 'Clock in failed'));
      // The server may know better than we do (e.g. clocked in on another device).
      load();
    } finally {
      setPunching(false);
    }
  };

  const unpaidEntries = [...(summary?.unpaid_entries || [])].reverse();
  const lastPayment = summary?.last_payment;

  return (
    // Pinned layout: where it all fits, the cards stay put and only the shift
    // list scrolls. On a phone the cards alone fill the screen, so the list keeps
    // a minimum height and the page scrolls down to it instead.
    <Box sx={{ ...tablePageRootSx, overflowY: 'auto' }}>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        Time Clock
      </Typography>

      {loading ? (
        <TimeClockSkeleton />
      ) : (
        <>
          {/* Clock in / out */}
          <Paper sx={{ p: 3, mb: 2, borderRadius: 2, textAlign: 'center' }}>
            {openEntry ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  Clocked in at {formatTime(openEntry.clock_in)}
                </Typography>
                <Typography variant="h3" fontWeight={700} sx={{ my: 1, ...tabularSx }}>
                  {elapsed}
                </Typography>
              </>
            ) : (
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                You are clocked out.
              </Typography>
            )}
            <Button
              fullWidth
              size="large"
              variant="contained"
              color={openEntry ? 'error' : 'primary'}
              startIcon={openEntry ? <LogoutIcon /> : <LoginIcon />}
              onClick={handlePunch}
              disabled={punching}
              sx={{ py: 2, fontSize: '1.1rem' }}
            >
              {openEntry ? 'Clock Out' : 'Clock In'}
            </Button>
          </Paper>

          {/* Hours since last paid */}
          <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Typography variant="overline" color="text.secondary" lineHeight={1}>
              Hours since last paid
            </Typography>
            <Typography variant="h3" component="p" fontWeight={700} sx={{ my: 0.5, ...tabularSx }}>
              {formatHours(summary?.unpaid_hours)}
            </Typography>

            {/* How the total splits — only the rows that say something */}
            {summary?.carryover_hours > 0 && (
              <>
                <BreakdownRow label="Worked" value={formatHours(summary.worked_hours)} />
                <BreakdownRow label="Carried over" value={formatHours(summary.carryover_hours)} />
              </>
            )}
            {summary?.payable_hours > 0 ? (
              <>
                <Divider sx={{ my: 1 }} />
                <BreakdownRow label="Ready to pay" value={formatHours(summary.payable_hours)} strong />
                {summary.carryover_after_pay > 0 && (
                  <BreakdownRow label="Rolls to next pay" value={formatHours(summary.carryover_after_pay)} />
                )}
              </>
            ) : summary?.unpaid_hours > 0 && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body1">
                  Under half an hour so far. It rolls into your next pay.
                </Typography>
              </>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              Paid in half-hour steps · {lastPayment
                ? `Last paid ${formatDate(lastPayment.paid_date)}`
                : 'Not paid yet'}
            </Typography>
            {openEntry && (
              <Typography variant="body2" color="text.secondary">
                Your current shift is added when you clock out.
              </Typography>
            )}
          </Paper>

          {/* Unpaid shifts */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 1 }}>
            <Typography variant="overline" fontWeight={700} color="text.secondary" lineHeight={1}>
              Shifts
            </Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>
          {unpaidEntries.length === 0 ? (
            <Typography color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              No shifts since your last payment.
            </Typography>
          ) : (
            <Box sx={{ ...tablePageScrollRegionSx, minHeight: 320 }}>
              {/* Hug the rows rather than stretch: a few shifts shouldn't sit in a tall empty box. */}
              <TableContainer component={Paper} sx={{ ...tablePageTableContainerSx, flex: '0 1 auto', height: 'auto' }}>
                {/* Compact rows like the admin shift table; nowrap keeps "9:05 AM" on one line, and
                    halved side padding on phones lets all four columns fit without wrapping. */}
                <Table size="small" stickyHeader sx={{ '& td, & th': { whiteSpace: 'nowrap', px: { xs: 1, sm: 2 } } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>In</TableCell>
                      <TableCell>Out</TableCell>
                      <TableCell align="right">Hours</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {unpaidEntries.map((entry) => (
                      <TableRow key={entry.id} hover>
                        <TableCell>{formatShortDate(entry.clock_in)}</TableCell>
                        <TableCell>{formatTime(entry.clock_in)}</TableCell>
                        <TableCell>{formatTime(entry.clock_out)}</TableCell>
                        <TableCell align="right" sx={tabularSx}>{Number(entry.hours || 0).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
